/**
 * RBAC-4.1 · The Role block on a person's record (FR-30, FR-34, UX-DR15).
 *
 * Lists every role the person holds with where it comes from (AC 3), states which role system they
 * are on so an admin knows a change replaces rather than adds (AC 2), and opens the change dialog
 * (AC 1), and opens RBAC-4.2's copy-access dialog beside it. Shown only to a caller holding `roles.administer`; the database refuses anyone else
 * (`person_role_assignment`, 42501).
 *
 * RBAC-4.3 adds the two flows for the day somebody leaves: Remove access now on someone else's record
 * (offered exactly when Change role is), and Hand over administration on your own, which is the one
 * sanctioned way to change your own role.
 *
 * RBAC-4.4 adds "What can they do?" underneath: every permission they hold, each with its origin.
 *
 * ⚠️ It replaces the Role select in the Edit User panel. That select saved through
 * `update-admin-user` with the rest of the form, on the service key, with no preview; while this card
 * is shown the form no longer sends `role_id`, so there is exactly one way to change a role here.
 */
import { useCallback, useEffect, useState } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  assignPersonRoleErrorMessage,
  cannotChangeSentence,
  originLabel,
  personNotHomedHere,
  roleSystemStatement,
  type PersonRoleAssignment,
} from '@/lib/personRole';
import { PersonRoleError, fetchPersonRoleAssignment } from '@/services/personRoleService';
import { ChangeRoleDialog } from './ChangeRoleDialog';
import { CopyAccessDialog } from './CopyAccessDialog';
import { EffectivePermissionsSection } from './EffectivePermissionsSection';
import { HandoverDialog } from './HandoverDialog';
import { RemoveAccessDialog } from './RemoveAccessDialog';
import { usePermissions } from '@/contexts/PermissionContext';
import { SELF_ROLE_SENTENCE } from '@/lib/personAccess';

export interface PersonRoleCardProps {
  userId: string;
  /** Called after a change with the new role id, so the form and the list can follow. */
  onChanged?: (roleId: number) => void;
  /** RBAC-4.3: after their access was removed. The record should close: they are off the list. */
  onRemoved?: () => void;
}

export function PersonRoleCard({ userId, onChanged, onRemoved }: PersonRoleCardProps) {
  const { refetch: refetchPermissions } = usePermissions();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [assignment, setAssignment] = useState<PersonRoleAssignment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elsewhere, setElsewhere] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setElsewhere(false);
    try {
      setAssignment(await fetchPersonRoleAssignment(userId));
    } catch (e) {
      const rpc = e instanceof PersonRoleError ? e.rpc : { message: e instanceof Error ? e.message : undefined };
      setAssignment(null);
      if (personNotHomedHere(rpc)) setElsewhere(true);
      else setError(assignPersonRoleErrorMessage(rpc));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (elsewhere) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="person-role-elsewhere">
        This person belongs to another account. Their role is changed there.
      </p>
    );
  }

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }

  if (!assignment) {
    return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  }

  const refusal = cannotChangeSentence(assignment);

  return (
    <div className="space-y-3" data-testid="person-role-card">
      {assignment.roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">No role yet.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="person-role-list">
          {assignment.roles.map((r, i) => (
            <li key={`${r.origin}-${r.roleId}-${i}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-medium">{r.name}</span>
              {!r.isGlobal && <Badge variant="secondary">Your account's own role</Badge>}
              <span className="text-xs text-muted-foreground">{originLabel(r)}</span>
            </li>
          ))}
        </ul>
      )}

      <div
        className="flex gap-2 rounded-md border border-sky-200 bg-sky-50 p-2.5 text-sm text-sky-900 dark:border-sky-500/40 dark:bg-sky-950/40 dark:text-sky-100"
        data-testid="person-role-system"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p>{roleSystemStatement(assignment)}</p>
      </div>

      {!assignment.isActive && (
        <p className="text-sm font-medium" data-testid="person-access-removed">
          Their access has been removed. Restore it from Users → Access removed.
        </p>
      )}

      {assignment.cannotChangeReason === 'self' ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{SELF_ROLE_SENTENCE}</p>
          {/* RBAC-4.3 · FR-33a. The options say whether you may, and the dialog says why not. */}
          <Button type="button" variant="outline" size="sm" onClick={() => setHandoverOpen(true)}>
            Hand over administration
          </Button>
        </div>
      ) : refusal ? (
        <p className="text-sm text-muted-foreground">{refusal}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
            Change role
          </Button>
          {/* RBAC-4.2: the same refusals as Change role, so it is offered exactly when that is. */}
          <Button type="button" variant="outline" size="sm" onClick={() => setCopyOpen(true)}>
            Copy access from someone
          </Button>
          {assignment.isActive && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setRemoveOpen(true)}
            >
              Remove access now
            </Button>
          )}
        </div>
      )}

      {/* RBAC-4.4: what they can do, with where each permission comes from. */}
      <EffectivePermissionsSection userId={assignment.userId} firstName={assignment.firstName} version={assignment} />

      <RemoveAccessDialog
        open={removeOpen}
        userId={assignment.userId}
        firstName={assignment.firstName}
        onOpenChange={setRemoveOpen}
        onRemoved={() => {
          void load();
          onRemoved?.();
        }}
      />

      <HandoverDialog
        open={handoverOpen}
        onOpenChange={setHandoverOpen}
        onHandedOver={(roleId) => {
          onChanged?.(roleId);
          // You no longer administer: the screen should stop saying you do now, not in a minute.
          void refetchPermissions();
        }}
      />

      <CopyAccessDialog
        open={copyOpen}
        assignment={assignment}
        onOpenChange={setCopyOpen}
        onChanged={(roleId) => {
          void load();
          if (roleId !== null) onChanged?.(roleId);
        }}
      />

      <ChangeRoleDialog
        open={dialogOpen}
        assignment={assignment}
        onOpenChange={setDialogOpen}
        onChanged={(roleId) => {
          void load();
          if (roleId !== null) onChanged?.(roleId);
        }}
      />
    </div>
  );
}
